import { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Globe } from 'lucide-react';
import { useNavigateWithParams } from '../hooks/useNavigateWithParams';
import { db } from '../firebase';
import { appId } from '../constants';
import { collection, query, where, orderBy, limit } from 'firebase/firestore';
import { safeOnSnapshot } from '../utils';
import Avatar from './Avatar';
import { useAuth } from '../contexts/AuthContext';

const MobileChatList = ({
  state,
  allUsers,
  getDmUser,
  handleGoHome,
  handleGoTikiBar,
  handleStartDM,
  handleShowProfile,
  channels,
  user,
}) => {
  const navigate = useNavigateWithParams();
  const [lastMessages, setLastMessages] = useState({});

  const { profile } = useAuth();
  
  const dmChannels = useMemo(
    () =>
      (channels || []).filter((c) => c.type === 'dm' && (c.participants || []).includes(user?.uid)),
    [channels, user?.uid]
  );
  
  const otherUsers = useMemo(
    () => {
      const myFriends = profile?.friends || [];
      const myEmail = user?.email?.toLowerCase();
      
      return (allUsers || []).filter((u) => {
        if (u.id === user?.uid) return false;
        const theirEmail = u.email?.toLowerCase();
        const theirFriends = u.friends || [];
        return myFriends.includes(theirEmail) && theirFriends.includes(myEmail);
      });
    },
    [allUsers, user?.uid, profile?.friends, user?.email]
  );

  // Fetch last message for each potential DM channel and Tiki Bar
  useEffect(() => {
    if (!user) return;

    // Fetch for existing DM channels
    const unsubs = dmChannels.map((dm) => {
      const q = query(
        collection(db, 'artifacts', appId, 'public', 'data', 'messages'),
        where('channelId', '==', dm.id),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      return safeOnSnapshot(q, (snap) => {
        if (!snap.empty) {
          setLastMessages((prev) => ({
            ...prev,
            [dm.id]: snap.docs[0].data(),
          }));
        }
      });
    });

    // Also fetch for Tiki Bar
    const qTiki = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'messages'),
      where('channelId', '==', 'general_chat_global'),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    const unsubTiki = safeOnSnapshot(qTiki, (snap) => {
      if (!snap.empty) {
        setLastMessages((prev) => ({
          ...prev,
          general_chat_global: snap.docs[0].data(),
        }));
      }
    });

    return () => {
      unsubs.forEach((unsub) => unsub());
      unsubTiki();
    };
  }, [user, dmChannels]);

  // Combined list: Users we have a DM with + users we don't
  const chatEntries = useMemo(() => {
    return otherUsers
      .map((u) => {
        const dm = dmChannels.find(
          (c) => (c.participants || []).includes(u.id) && (c.participants || []).includes(user?.uid)
        );
        const lastMsg = dm ? lastMessages[dm.id] : null;
        // Check who performed the last action (message OR reaction)
        const isMeLast = dm?.lastActorId === user?.uid;

        return {
          user: u,
          dm: dm,
          lastMsg: lastMsg,
          isMeLast: isMeLast,
          lastActive: lastMsg?.createdAt?.seconds || 0,
        };
      })
      .sort((a, b) => b.lastActive - a.lastActive);
  }, [otherUsers, dmChannels, lastMessages, user?.uid]);

  const formatShortDate = (seconds) => {
    if (!seconds) return '';
    const d = new Date(seconds * 1000);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const tikiBarChannel = (channels || []).find((c) => c.id === 'general_chat_global');
  const isMeLastTiki = tikiBarChannel?.lastActorId === user?.uid;

  return (
    <div className="flex flex-col bg-white h-full overflow-y-auto">
      <header className="pt-12 pb-4 px-6 flex items-center justify-between border-b border-zinc-100 sticky top-0 bg-white z-10">
        <div className="flex items-center gap-4">
          <button onClick={handleGoHome} className="p-1 -ml-1 text-zinc-500">
            <ArrowLeft size={24} />
          </button>
          <h1 className="font-black text-zinc-900 text-xl tracking-tight uppercase">Chat</h1>
        </div>
      </header>

      <main className="flex-1 flex flex-col">
        {/* Global Tiki Bar */}
        <button
          data-testid="project-item-general_chat_global"
          onClick={handleGoTikiBar}
          className="flex items-center justify-between px-6 py-5 border-b border-zinc-50 active:bg-zinc-50"
        >
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 rounded-2xl bg-zinc-900 flex items-center justify-center text-white shadow-lg shadow-zinc-200 flex-shrink-0">
              <Globe size={24} />
            </div>
            <div className="flex flex-col text-left min-w-0">
              <span className="font-black text-zinc-900 text-lg uppercase tracking-tight">
                Tiki Bar
              </span>
              <span className="text-xs text-zinc-400 font-bold truncate">
                Global Community Chat
              </span>
            </div>
          </div>
          {lastMessages['general_chat_global'] && (
            <div className="flex flex-col items-end flex-shrink-0 ml-2">
              <span
                className={`text-[10px] font-black ${isMeLastTiki ? 'text-green-500' : 'text-red-500'}`}
              >
                {formatShortDate(lastMessages['general_chat_global'].createdAt?.seconds)}
              </span>
            </div>
          )}
        </button>

        {/* Private Messages Header */}
        <div className="bg-zinc-50 px-6 py-2 border-b border-zinc-100">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
            Private Messages
          </span>
        </div>

        {/* Combined User/DM List */}
        {chatEntries.map((entry) => {
          const { user: targetUser, dm, lastMsg, isMeLast } = entry;

          // Actual online status: lastSeen within 5 minutes
          const lastSeenSec = targetUser.lastSeen?.seconds || 0;
          // eslint-disable-next-line react-hooks/purity
          const isOnline = Date.now() - lastSeenSec * 1000 < 300000;

          return (
            <button
              key={targetUser.id}
              data-testid={`user-item-${targetUser.id}`}
              onClick={() => {
                if (dm) navigate(`/p/${dm.id}/chat${window.location.search}`);
                else handleStartDM(targetUser);
              }}
              className="flex items-center justify-between px-6 py-4 border-b border-zinc-50 active:bg-zinc-50"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div
                  className="relative flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShowProfile(targetUser);
                  }}
                >
                  <Avatar src={targetUser.avatar} size={48} className="rounded-2xl shadow-sm" />
                  <div
                    className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white shadow-sm ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}
                  />
                </div>
                <div className="flex flex-col text-left min-w-0">
                  <span className="font-bold text-zinc-800 text-lg truncate">
                    {targetUser.name}
                  </span>
                  <span className="text-xs text-zinc-400 font-medium truncate">
                    {lastMsg
                      ? lastMsg.isImage
                        ? 'Sent an image'
                        : lastMsg.text.replace(/<[^>]*>/g, '')
                      : 'Tap to message'}
                  </span>
                </div>
              </div>
              {lastMsg && (
                <div className="flex flex-col items-end flex-shrink-0 ml-2">
                  <span
                    className={`text-[10px] font-black ${isMeLast ? 'text-green-500' : 'text-red-500'}`}
                  >
                    {formatShortDate(lastMsg.createdAt?.seconds)}
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </main>
    </div>
  );
};

export default MobileChatList;
